/**
 * How the DGI MCF checks and splits one 31h article (SECEF BF ST 1.0 §4.4),
 * as measured on the device. Amounts are integer hundredths, QT integer
 * thousandths, the rate in hundredths of a percent (18 % = 1800).
 *
 * - TS goes before VAT; the MCF adds its VAT: the TS of the reply is
 *   TS × (1 + rate) rounded once on the line. The article amount is MON
 *   less that TS.
 * - TTC mode: MON is accepted when PR × QT, to the centime, is the article
 *   amount; MH is QT × (PR / (1 + rate) to four decimals), to the centime,
 *   MT the rest.
 * - HT mode: MON is accepted when PR × QT × (1 + rate), to the centime, is
 *   the article amount; MH is PR × QT to the centime, MT the rest.
 * - The acceptance products are exact before their one rounding; the MCF
 *   tolerates no centime of difference on MON, either way.
 * - With PRORIG, the MCF splits the gross amount G = PRORIG × QT and the
 *   price change D = G − (net amount) apart, each rounded: TTC mode, MH =
 *   QT × (PRORIG / (1 + rate) to four decimals) − D / (1 + rate), each to
 *   the centime; HT mode, the article amount must be G × (1 + rate) −
 *   D × (1 + rate), and MH = G − D (hypothèse: G with three decimals and
 *   PRORIG with TS not measured).
 *
 * @module @efacture/mcf-protocol/secef-item-rule
 */
/** num / den rounded to an integer with `mode`; num and den non-negative. */
export function roundSecefDivision(num, den, mode) {
    const quotient = num / den;
    const remainder = num % den;
    if (remainder === 0n)
        return quotient;
    switch (mode) {
        case "floor":
            return quotient;
        case "ceil":
            return quotient + 1n;
        case "half-up":
            return remainder * 2n >= den ? quotient + 1n : quotient;
    }
}
/** num / den rounded to an integer with `mode` applied to the magnitude; den positive. */
function roundSigned(num, den, mode) {
    return num < 0n ? -roundSecefDivision(-num, den, mode) : roundSecefDivision(num, den, mode);
}
/**
 * MH of a TTC-mode article: QT × (the unit price / (1 + rate) to four
 * decimals), to the centime, less D / (1 + rate) to the centime when the
 * article carries a price change. Hundredths.
 */
export function secefTtcTaxable(input, rounding = "half-up") {
    const withVat = 10000n + BigInt(input.rate);
    // The unit price / (1 + rate) in ten-thousandths of the currency unit.
    const unitTaxable = roundSigned(input.unitPrice * 1000000n, (input.unitPriceDivisor ?? 1n) * withVat, rounding);
    let taxable = roundSigned(unitTaxable * input.quantity, (input.quantityDivisor ?? 1n) * 100000n, rounding);
    const change = input.change ?? 0n;
    if (change !== 0n)
        taxable -= roundSigned(change * 10n, (input.changeDivisor ?? 1n) * withVat, rounding);
    return Number(taxable);
}
/**
 * The article amount (MON less the TS with its VAT) the MCF accepts for an
 * HT-mode article: G × (1 + rate) to the centime, less D × (1 + rate) to
 * the centime with a price change. Hundredths.
 */
export function secefHtArticle(input, rounding = "half-up") {
    const withVat = 10000n + BigInt(input.rate);
    const change = input.change ?? 0n;
    const article = roundSigned(input.gross * withVat, 10000000n, rounding);
    return Number(change === 0n ? article : article - roundSigned(change * withVat, 10000000n, rounding));
}
/** The 31h figures the DGI MCF returns for one article; null when it refuses the amount. */
export function secefItemFigures(input, rounding = "half-up") {
    const { priceMode, unitPrice, quantity, amount, specificTax, originalPrice } = input;
    if (unitPrice <= 0 || quantity <= 0 || amount <= 0 || specificTax < 0)
        return null;
    if (originalPrice !== undefined && originalPrice <= 0)
        return null;
    const rate = BigInt(input.rate);
    const withVat = 10000n + rate;
    const specificTaxWithVat = Number(roundSecefDivision(BigInt(specificTax) * withVat, 10000n, rounding));
    const article = amount - specificTaxWithVat;
    if (article <= 0)
        return null;
    // Products in hundredths × 1000.
    const product = BigInt(unitPrice) * BigInt(quantity);
    const gross = originalPrice === undefined ? null : BigInt(originalPrice) * BigInt(quantity);
    let taxable;
    if (priceMode === "TTC") {
        if (Number(roundSecefDivision(product, 1000n, rounding)) !== article)
            return null;
        taxable = secefTtcTaxable({
            rate: input.rate,
            unitPrice: BigInt(originalPrice ?? unitPrice),
            quantity: BigInt(quantity),
            ...(gross !== null ? { change: gross - BigInt(article) * 1000n } : {}),
        }, rounding);
    }
    else if (gross === null) {
        if (secefHtArticle({ rate: input.rate, gross: product }, rounding) !== article)
            return null;
        taxable = Number(roundSecefDivision(product, 1000n, rounding));
    }
    else {
        const change = gross - product;
        if (secefHtArticle({ rate: input.rate, gross, change }, rounding) !== article)
            return null;
        taxable = Number(roundSigned(gross, 1000n, rounding) - roundSigned(change, 1000n, rounding));
    }
    return { total: amount, taxable, vat: article - taxable, specificTax: specificTaxWithVat };
}
